// File: /var/www/app1/frontend/src/components/UserManagement.tsx

import React, { useState, useEffect, FormEvent, ChangeEvent } from "react";
import axios, { AxiosError } from "axios";
import { User, UserManagementUser, ApiResponse } from "../types";

interface UserManagementProps {
  user: User;
  onImpersonate?: (token: string, targetUser: { id: number; username: string; role: string }) => void;
}

interface UserFormData {
  username: string;
  email: string;
  role: "reporter" | "admin" | "missionary";
  missionaryNumber: string;
  password: string;
  confirmPassword: string;
  isActive: boolean;
}

interface MissionaryOption {
  id: number;
  missionary_number: string;
}

interface Assignment {
  assignment_id: number;
  missionary_id: number;
  missionary_number: string;
  missionary_active: boolean;
  assigned_at: string;
  assigned_by_username: string | null;
}

const UserManagement: React.FC<UserManagementProps> = ({ user, onImpersonate }) => {
  const [users, setUsers] = useState<UserManagementUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<UserManagementUser | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Assignment state
  const [allMissionaries, setAllMissionaries] = useState<MissionaryOption[]>([]);
  const [selectedMissionaryIds, setSelectedMissionaryIds] = useState<number[]>([]);
  const [currentAssignments, setCurrentAssignments] = useState<Assignment[]>([]);
  const [showAssignments, setShowAssignments] = useState<number | null>(null);
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);

  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    email: "",
    role: "reporter",
    missionaryNumber: "",
    password: "",
    confirmPassword: "",
    isActive: true,
  });

  useEffect(() => {
    if (user.role === "admin" || user.role === "superadmin") {
      fetchUsers();
      fetchMissionaries();
    }
  }, [user.role]);

  // Check if current user is admin
  if (user.role !== "admin" && user.role !== "superadmin") {
    return (
      <div style={{ padding: "40px", color: "white", textAlign: "center" }}>
        <h2>Access Denied</h2>
        <p>Only administrators can manage users.</p>
      </div>
    );
  }

  const fetchUsers = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await axios.get<{ users: UserManagementUser[] }>("/api/admin/users");
      setUsers(response.data.users || []);
      setError("");
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchMissionaries = async (): Promise<void> => {
    try {
      const response = await axios.get("/api/missionaries");
      if (response.data.missionaries) {
        setAllMissionaries(response.data.missionaries.map((m: any) => ({
          id: m.id,
          missionary_number: m.missionary_number,
        })));
      }
    } catch (error) {
      console.error("Failed to fetch missionaries:", error);
    }
  };

  const fetchAssignments = async (userId: number): Promise<void> => {
    setAssignmentLoading(true);
    try {
      const response = await axios.get(`/api/admin/users/${userId}/assignments`);
      setCurrentAssignments(response.data.assignments || []);
    } catch (error) {
      console.error("Failed to fetch assignments:", error);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const resetForm = (): void => {
    setFormData({
      username: "",
      email: "",
      role: "reporter",
      missionaryNumber: "",
      password: "",
      confirmPassword: "",
      isActive: true,
    });
    setSelectedMissionaryIds([]);
    setEditingUser(null);
    setShowAddForm(false);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const toggleMissionarySelection = (missionaryId: number): void => {
    setSelectedMissionaryIds(prev =>
      prev.includes(missionaryId)
        ? prev.filter(id => id !== missionaryId)
        : [...prev, missionaryId]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation for new users
    if (!editingUser) {
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (formData.password.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }
    }

    // Missionary number required for legacy missionary role
    if (formData.role === "missionary" && !formData.missionaryNumber) {
      setError("Missionary number is required for missionary role");
      return;
    }

    setLoading(true);
    try {
      if (!editingUser) {
        // Create new user
        const payload: any = {
          username: formData.username,
          role: formData.role,
          generatePassword: false,
          customPassword: formData.password,
        };

        if (formData.role === "missionary") {
          payload.missionaryNumber = formData.missionaryNumber;
        }

        if (formData.role === "reporter" && selectedMissionaryIds.length > 0) {
          payload.missionaryIds = selectedMissionaryIds;
        }

        const response = await axios.post<ApiResponse<{ temporaryPassword?: string }>>(
          "/api/admin/users",
          payload
        );

        if (response.data.success) {
          setSuccess(`User ${formData.username} created successfully!`);
          resetForm();
          fetchUsers();
        }
      } else {
        // Update existing user
        const payload: any = {
          role: formData.role,
          isActive: formData.isActive,
        };

        // Only include password if provided
        if (formData.password) {
          await axios.post(`/api/admin/users/${editingUser.id}/reset-password`, {
            newPassword: formData.password,
            generatePassword: false,
          });
        }

        const response = await axios.put<ApiResponse>(
          `/api/admin/users/${editingUser.id}`,
          payload
        );

        if (response.data.success) {
          setSuccess(`User ${formData.username} updated successfully!`);
          resetForm();
          fetchUsers();
        }
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (userToEdit: UserManagementUser): void => {
    setFormData({
      username: userToEdit.username,
      email: userToEdit.email || "",
      role: userToEdit.role as "reporter" | "admin" | "missionary",
      missionaryNumber: userToEdit.missionary_number || "",
      password: "",
      confirmPassword: "",
      isActive: userToEdit.is_active,
    });
    setSelectedMissionaryIds([]);
    setEditingUser(userToEdit);
    setShowAddForm(true);
    setError("");
    setSuccess("");
  };

  const handleToggleActive = async (
    userId: number,
    currentStatus: boolean
  ): Promise<void> => {
    setLoading(true);
    try {
      const response = await axios.put<ApiResponse>(`/api/admin/users/${userId}`, {
        isActive: !currentStatus,
      });

      if (response.data.success) {
        setSuccess(`User ${currentStatus ? "deactivated" : "activated"} successfully!`);
        fetchUsers();
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId: number, username: string): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete<ApiResponse>(`/api/admin/users/${userId}`);

      if (response.data.success) {
        setSuccess(`User ${username} deleted successfully`);
        fetchUsers();
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId: number, username: string): Promise<void> => {
    const choice = window.prompt(
      `Reset password for ${username}?\n\n` +
        `Enter "1" to generate a temporary password\n` +
        `Enter "2" to choose a custom password\n` +
        `Or click Cancel to abort`,
      "1"
    );

    if (!choice) return;

    let generatePassword = true;
    let customPassword = "";

    if (choice === "2") {
      customPassword =
        window.prompt(
          `Enter a custom password for ${username}:\n\n` +
            `Requirements:\n` +
            `- Minimum 8 characters\n` +
            `- At least one uppercase letter\n` +
            `- At least one lowercase letter\n` +
            `- At least one number`
        ) || "";

      if (!customPassword) {
        alert("Password reset cancelled - no password entered");
        return;
      }
      generatePassword = false;
    }

    setLoading(true);
    try {
      const payload: any = { generatePassword };
      if (!generatePassword) {
        payload.newPassword = customPassword;
      }

      const response = await axios.post<ApiResponse<{ temporaryPassword?: string }>>(
        `/api/admin/users/${userId}/reset-password`,
        payload
      );

      if (response.data.success) {
        if (response.data.data?.temporaryPassword) {
          alert(
            `Password reset successful!\nNew temporary password: ${response.data.data.temporaryPassword}\n\nPlease save this password and share it with the user.`
          );
        } else {
          alert(`Password reset successful!\nThe user can now log in with the custom password you set.`);
        }
        setSuccess(`Password reset for ${username}`);
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockUser = async (userId: number, username: string): Promise<void> => {
    if (!window.confirm(`Unlock account for ${username}?`)) return;

    setLoading(true);
    try {
      const response = await axios.post<ApiResponse>(`/api/admin/users/${userId}/unlock`);

      if (response.data.success) {
        setSuccess(`User ${username} unlocked successfully!`);
        fetchUsers();
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to unlock user");
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (userId: number, username: string): Promise<void> => {
    if (!window.confirm(`Login as ${username}? You will see the app as they do.`)) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/admin/impersonate/${userId}`);
      if (response.data.success && onImpersonate) {
        onImpersonate(response.data.impersonationToken, response.data.targetUser);
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to impersonate user");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAssignments = async (userId: number): Promise<void> => {
    if (showAssignments === userId) {
      setShowAssignments(null);
      setCurrentAssignments([]);
      return;
    }
    setShowAssignments(userId);
    await fetchAssignments(userId);
  };

  const handleAddAssignment = async (userId: number, missionaryId: number): Promise<void> => {
    setAssignmentLoading(true);
    try {
      await axios.post(`/api/admin/users/${userId}/assignments`, {
        missionaryIds: [missionaryId],
      });
      await fetchAssignments(userId);
      setSuccess("Missionary assigned");
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to assign missionary");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleRemoveAssignment = async (userId: number, missionaryId: number): Promise<void> => {
    setAssignmentLoading(true);
    try {
      await axios.delete(`/api/admin/users/${userId}/assignments/${missionaryId}`);
      await fetchAssignments(userId);
      setSuccess("Assignment removed");
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      setError(axiosError.response?.data?.error || "Failed to remove assignment");
    } finally {
      setAssignmentLoading(false);
    }
  };

  const isLocked = (u: UserManagementUser): boolean => {
    return !!(u.locked_until && new Date(u.locked_until) > new Date());
  };

  const isSuperadmin = user.role === 'superadmin';

  // Filter users based on search
  const filteredUsers = users.filter(
    (u) => {
      return (
        u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.missionary_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.role?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  );

  const getRoleBadgeColor = (role: string): string => {
    switch (role) {
      case 'superadmin': return '#6b21a8';
      case 'admin': return '#805ad5';
      case 'reporter': return '#2b6cb0';
      default: return '#4299e1';
    }
  };

  if (loading && users.length === 0) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "40px 20px" }}>
      <h2 style={{ color: "white", marginBottom: "30px" }}>User Management</h2>

      {/* Add/Edit User Form */}
      {showAddForm && (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "25px",
            marginBottom: "30px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h3 style={{ marginBottom: "20px", color: "#2d3748" }}>
            {editingUser ? "Edit User" : "Add New User"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Username *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingUser}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: editingUser ? "#f7fafc" : "white",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Role *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <option value="reporter">Reporter</option>
                  <option value="admin">Admin</option>
                  {isSuperadmin && <option value="missionary">Missionary (legacy)</option>}
                </select>
              </div>

              {formData.role === "missionary" && (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontWeight: "500",
                    }}
                  >
                    Missionary Number *
                  </label>
                  <input
                    type="text"
                    name="missionaryNumber"
                    value={formData.missionaryNumber}
                    onChange={handleInputChange}
                    required={formData.role === "missionary"}
                    disabled={!!editingUser}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: editingUser ? "#f7fafc" : "white",
                    }}
                  />
                </div>
              )}

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Password {!editingUser && "*"}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUser}
                  placeholder={editingUser ? "Leave blank to keep current password" : ""}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Confirm Password {!editingUser && "*"}
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={!editingUser}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />
              </div>

              {editingUser && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    style={{ marginRight: "8px" }}
                  />
                  <label>Active</label>
                </div>
              )}
            </div>

            {/* Missionary assignments for new reporter */}
            {formData.role === "reporter" && !editingUser && (
              <div style={{ marginTop: "20px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Assign Missionaries (optional - can be done later)
                </label>
                <div style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  padding: "8px",
                }}>
                  {allMissionaries.map(m => (
                    <label key={m.id} style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "4px 8px",
                      cursor: "pointer",
                      backgroundColor: selectedMissionaryIds.includes(m.id) ? "#ebf8ff" : "transparent",
                      borderRadius: "3px",
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedMissionaryIds.includes(m.id)}
                        onChange={() => toggleMissionarySelection(m.id)}
                        style={{ marginRight: "8px" }}
                      />
                      Worker #{m.missionary_number}
                    </label>
                  ))}
                </div>
                {selectedMissionaryIds.length > 0 && (
                  <div style={{ marginTop: "5px", fontSize: "13px", color: "#4a5568" }}>
                    {selectedMissionaryIds.length} missionary(ies) selected
                  </div>
                )}
              </div>
            )}

            {error && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#fed7d7",
                  color: "#c53030",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#c6f6d5",
                  color: "#276749",
                  borderRadius: "4px",
                }}
              >
                {success}
              </div>
            )}

            <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#48bb78",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {editingUser ? "Update User" : "Create User"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#718096",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Action Bar */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {isSuperadmin && (
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4299e1",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            + Add New User
          </button>
        )}

        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          style={{
            padding: "8px 12px",
            border: "1px solid #ddd",
            borderRadius: "4px",
            width: "300px",
          }}
        />
      </div>

      {/* Messages outside of form */}
      {!showAddForm && error && (
        <div
          style={{
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "#fed7d7",
            color: "#c53030",
            borderRadius: "4px",
          }}
        >
          {error}
        </div>
      )}
      {!showAddForm && success && (
        <div
          style={{
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "#c6f6d5",
            color: "#276749",
            borderRadius: "4px",
          }}
        >
          {success}
        </div>
      )}

      {/* Users Table */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                backgroundColor: "#f7fafc",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <th style={{ padding: "12px", textAlign: "left" }}>Username</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Role</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Missionary #</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Last Login</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#718096",
                  }}
                >
                  {searchTerm ? "No users found matching your search" : "No users found"}
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <React.Fragment key={u.id}>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px" }}>
                      {u.username}
                      {isLocked(u) && (
                        <span style={{ color: "red", marginLeft: "5px" }}>🔒</span>
                      )}
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          backgroundColor: getRoleBadgeColor(u.role),
                          color: "white",
                        }}
                      >
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>{u.missionary_number || "-"}</td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: u.is_active ? "#c6f6d5" : "#fed7d7",
                          color: u.is_active ? "#276749" : "#c53030",
                        }}
                      >
                        {u.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      {u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}
                    </td>
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {isSuperadmin && (
                          <button
                            onClick={() => handleEdit(u)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#4299e1",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                            }}
                          >
                            Edit
                          </button>
                        )}
                        {u.role === "reporter" && (
                          <button
                            onClick={() => handleToggleAssignments(u.id)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: showAssignments === u.id ? "#2b6cb0" : "#63b3ed",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                            }}
                          >
                            {showAssignments === u.id ? "Hide" : "Assignments"}
                          </button>
                        )}
                        {u.role === "reporter" && u.is_active && onImpersonate && (
                          <button
                            onClick={() => handleImpersonate(u.id, u.username)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#d69e2e",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                            }}
                          >
                            Login As
                          </button>
                        )}
                        {isSuperadmin && (
                          <button
                            onClick={() => handleToggleActive(u.id, u.is_active)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: u.is_active ? "#ed8936" : "#48bb78",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                            }}
                          >
                            {u.is_active ? "Deactivate" : "Activate"}
                          </button>
                        )}
                        <button
                          onClick={() => handleResetPassword(u.id, u.username)}
                          disabled={loading}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#667eea",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: loading ? "not-allowed" : "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Reset PW
                        </button>
                        {isSuperadmin && isLocked(u) && (
                          <button
                            onClick={() => handleUnlockUser(u.id, u.username)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#38a169",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                            }}
                          >
                            Unlock
                          </button>
                        )}
                        {isSuperadmin && u.username !== user.username && (
                          <button
                            onClick={() => handleDelete(u.id, u.username)}
                            disabled={loading}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#f56565",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: loading ? "not-allowed" : "pointer",
                              fontSize: "14px",
                            }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Assignments panel */}
                  {showAssignments === u.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: "0" }}>
                        <div style={{
                          backgroundColor: "#f7fafc",
                          padding: "15px 20px",
                          borderBottom: "2px solid #e2e8f0",
                        }}>
                          <h4 style={{ margin: "0 0 10px 0", color: "#2d3748" }}>
                            Missionary Assignments for {u.username}
                          </h4>

                          {assignmentLoading ? (
                            <div>Loading assignments...</div>
                          ) : (
                            <>
                              {/* Current assignments */}
                              {currentAssignments.length > 0 ? (
                                <div style={{ marginBottom: "15px" }}>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                    {currentAssignments.map(a => (
                                      <span key={a.missionary_id} style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "5px 10px",
                                        backgroundColor: "#bee3f8",
                                        borderRadius: "4px",
                                        fontSize: "13px",
                                      }}>
                                        Worker #{a.missionary_number}
                                        <button
                                          onClick={() => handleRemoveAssignment(u.id, a.missionary_id)}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            color: "#c53030",
                                            cursor: "pointer",
                                            fontWeight: "bold",
                                            fontSize: "14px",
                                            padding: "0 2px",
                                          }}
                                          title="Remove assignment"
                                        >
                                          x
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div style={{ marginBottom: "15px", color: "#718096", fontStyle: "italic" }}>
                                  No missionaries assigned yet
                                </div>
                              )}

                              {/* Add assignment dropdown */}
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <select
                                  id={`add-assign-${u.id}`}
                                  style={{
                                    padding: "6px 10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    minWidth: "200px",
                                  }}
                                >
                                  <option value="">Select missionary to assign...</option>
                                  {allMissionaries
                                    .filter(m => !currentAssignments.some(a => a.missionary_id === m.id))
                                    .map(m => (
                                      <option key={m.id} value={m.id}>Worker #{m.missionary_number}</option>
                                    ))
                                  }
                                </select>
                                <button
                                  onClick={() => {
                                    const select = document.getElementById(`add-assign-${u.id}`) as HTMLSelectElement;
                                    if (select && select.value) {
                                      handleAddAssignment(u.id, parseInt(select.value));
                                      select.value = "";
                                    }
                                  }}
                                  style={{
                                    padding: "6px 14px",
                                    backgroundColor: "#48bb78",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                  }}
                                >
                                  + Assign
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Stats footer */}
      <div
        style={{
          marginTop: "20px",
          color: "white",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        Total Users: {filteredUsers.length} | Admins:{" "}
        {filteredUsers.filter((u) => u.role === "admin" || u.role === "superadmin").length} | Reporters:{" "}
        {filteredUsers.filter((u) => u.role === "reporter").length}
      </div>
    </div>
  );
};

export default UserManagement;
